import { memo, useEffect, useRef } from 'react';

import { flexRender } from '@tanstack/react-table';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { TableCell, tableCellClasses } from '@kc-console/common-ui';

import DataTableTableCellContent from './DataTableTableCellContent';

import type { JSX } from 'react';
import type { RowData } from '@tanstack/react-table';
import type { TextEllipsisState } from '../../hooks/DataTable/features';
import type { DataTableCellContent } from './types';

export interface DataTableTableBodyCellProps<TData extends RowData, TValue = unknown> {
  cell: DataTableCellContent<TData, TValue>;
  textEllipsis: TextEllipsisState;
  renderFallbackValue?: string;
}

export default memo(function DataTableTableBodyCell<TData extends RowData>(
  props: DataTableTableBodyCellProps<TData>,
) {
  const { cell, renderFallbackValue, textEllipsis } = props;

  const {
    id: cellId,
    isResizing,
    stickyPosition,
    meta,
    offset,
    CellTemplate,
    cellTemplateProps,
  } = cell;
  const { column: { align } = {}, cell: { padding, tooltipWhiteSpace } = {} } = meta ?? {};

  const contentRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const content = contentRef.current;
    if (content && !content.innerHTML && renderFallbackValue) {
      content.innerHTML = renderFallbackValue;
    }
  });

  return (
    <DataTableTableBodyCellRoot
      key={cellId}
      align={align}
      padding={padding}
      textEllipsis={textEllipsis}
      isResizing={isResizing}
      slots={{ content: DataTableTableCellContent }}
      slotProps={{ content: { whiteSpace: tooltipWhiteSpace } }}
      sticky={!!stickyPosition}
      stickyPosition={stickyPosition || undefined}
      style={{ ...(stickyPosition ? { [stickyPosition]: offset } : {}) }}
    >
      {flexRender(CellTemplate, cellTemplateProps)}
    </DataTableTableBodyCellRoot>
  );
}) as <TData extends RowData>(props: DataTableTableBodyCellProps<TData>) => JSX.Element;

const DataTableTableBodyCellRoot = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'textEllipsis',
})<{ textEllipsis?: boolean }>`
  & .${tableCellClasses.content} {
    overflow: hidden;
    ${({ textEllipsis }) =>
      textEllipsis
        ? css`
            text-overflow: ellipsis;
            white-space: nowrap;
          `
        : css`
            text-overflow: initial;
            white-space: wrap;
            word-break: break-all;
          `}
  }
`;
